/**
 * PATH: apps/quote/src/hooks/useBuilderEditsBuffer.ts
 * PURPOSE: Optimistic editing buffer with debounced bulk flush for Quote Builder
 *
 * WHY:
 * - Users expect instant UI feedback when editing cells (like Excel)
 * - Network latency shouldn't block typing flow
 * - Batching edits reduces API calls and improves performance
 * - Error states need per-cell tracking for retry capability
 *
 * FLOW:
 *   User types → stagePatch() → Optimistic UI update → debounce 400ms
 *   → flush() → bulk_update API → reconcile() or markError()
 */

import { useCallback, useRef, useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { quoteApi } from '@/lib/quoteApi'
import type { QuoteLineItem, QuoteTotals } from '@/lib/quoteApi'

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type CellErrorState = 'none' | 'error' | 'retrying'

export interface StagedPatch {
  itemId: string
  sectionId: string
  patch: Record<string, unknown>
  timestamp: number
}

export interface CellError {
  itemId: string
  field: string
  message: string
  originalValue: unknown
  attemptedValue: unknown
}

export interface BufferState {
  staged: Map<string, StagedPatch>
  errors: Map<string, CellError>
  isFlushing: boolean
  lastFlush: Date | null
}

export interface UseBuilderEditsBufferOptions {
  quoteId: string
  debounceMs?: number
  onFlushStart?: () => void
  onFlushSuccess?: (totals: QuoteTotals) => void
  onFlushError?: (errors: Array<{ id: string; error: string }>) => void
  onPatchStaged?: () => void
}

export interface UseBuilderEditsBufferResult {
  // State
  hasPendingChanges: boolean
  pendingCount: number
  isFlushing: boolean
  errors: CellError[]
  
  // Actions
  stagePatch: (sectionId: string, itemId: string, patch: Record<string, unknown>) => void
  flush: () => Promise<void>
  clearError: (itemId: string, field: string) => void
  clearAllErrors: () => void
  retryErrors: () => Promise<void>
  
  // Paste support
  stagePasteCreate: (
    sectionId: string,
    existingItems: QuoteLineItem[],
    pasteData: Array<Record<string, unknown>>,
    startRow: number
  ) => Promise<void>
  
  // Get optimistic value for a cell
  getOptimisticValue: (itemId: string, field: string) => unknown | undefined
  getCellErrorState: (itemId: string, field: string) => CellErrorState
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const DEFAULT_DEBOUNCE_MS = 400

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export function useBuilderEditsBuffer({
  quoteId,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  onFlushStart,
  onFlushSuccess,
  onFlushError,
  onPatchStaged,
}: UseBuilderEditsBufferOptions): UseBuilderEditsBufferResult {
  const queryClient = useQueryClient()
  
  // State
  const [state, setState] = useState<BufferState>({
    staged: new Map(),
    errors: new Map(),
    isFlushing: false,
    lastFlush: null,
  })
  
  // Refs for debouncing
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const flushPromiseRef = useRef<Promise<void> | null>(null)
  
  // Group staged patches by section
  const groupBySection = useCallback((): Map<string, Array<{ id: string; patch: Record<string, unknown> }>> => {
    const grouped = new Map<string, Array<{ id: string; patch: Record<string, unknown> }>>()
    
    state.staged.forEach((staged) => {
      const key = staged.sectionId
      if (!grouped.has(key)) {
        grouped.set(key, [])
      }
      grouped.get(key)!.push({ id: staged.itemId, patch: staged.patch })
    })
    
    return grouped
  }, [state.staged])
  
  // Flush all staged patches
  const flush = useCallback(async () => {
    if (state.staged.size === 0 || state.isFlushing) return
    
    setState(prev => ({ ...prev, isFlushing: true }))
    onFlushStart?.()
    
    const grouped = groupBySection()
    const allErrors: Array<{ id: string; error: string }> = []
    let lastTotals: QuoteTotals | null = null
    
    try {
      // Process each section's updates
      for (const [sectionId, updates] of grouped) {
        try {
          const result = await quoteApi.bulkUpdateLineItems(quoteId, sectionId, updates)
          
          if (result.errors?.length) {
            allErrors.push(...result.errors)
          }
          
          if (result.quote_totals) {
            lastTotals = result.quote_totals
          }
        } catch (error: unknown) {
          // Mark all items in this section as errored
          updates.forEach(u => {
            allErrors.push({ id: u.id, error: error.message || 'Network error' })
          })
        }
      }
      
      // Update state
      setState(prev => {
        const newErrors = new Map(prev.errors)
        const newStaged = new Map<string, StagedPatch>()
        
        // Move failed items back to staged for retry
        allErrors.forEach(err => {
          const staged = prev.staged.get(err.id)
          if (staged) {
            newStaged.set(err.id, staged)
            Object.keys(staged.patch).forEach(field => {
              newErrors.set(`${err.id}:${field}`, {
                itemId: err.id,
                field,
                message: err.error,
                originalValue: undefined,
                attemptedValue: staged.patch[field],
              })
            })
          }
        })
        
        return {
          staged: newStaged,
          errors: newErrors,
          isFlushing: false,
          lastFlush: new Date(),
        }
      })
      
      // Invalidate quote query to get fresh data
      queryClient.invalidateQueries({ queryKey: ['quote', 'builder', quoteId] })
      
      if (allErrors.length > 0) {
        onFlushError?.(allErrors)
      } else if (lastTotals) {
        onFlushSuccess?.(lastTotals)
      }
    } catch (error) {
      setState(prev => ({ ...prev, isFlushing: false }))
      onFlushError?.([{ id: 'unknown', error: 'Flush failed' }])
    }
  }, [state.staged, state.isFlushing, quoteId, groupBySection, queryClient, onFlushStart, onFlushSuccess, onFlushError])
  
  // Stage a patch (debounced)
  const stagePatch = useCallback((sectionId: string, itemId: string, patch: Record<string, unknown>) => {
    setState(prev => {
      const existing = prev.staged.get(itemId)
      const newStaged = new Map(prev.staged)
      
      newStaged.set(itemId, {
        itemId,
        sectionId,
        patch: existing ? { ...existing.patch, ...patch } : patch,
        timestamp: Date.now(),
      })
      
      return { ...prev, staged: newStaged }
    })
    
    onPatchStaged?.()
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    
    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      flush()
    }, debounceMs)
  }, [debounceMs, flush, onPatchStaged])
  
  // Paste-create: handle paste that may exceed existing rows
  const stagePasteCreate = useCallback(async (
    sectionId: string,
    existingItems: QuoteLineItem[],
    pasteData: Array<Record<string, unknown>>,
    startRow: number
  ) => {
    const existingCount = existingItems.length
    const neededRows = startRow + pasteData.length
    
    // Create new rows if needed
    if (neededRows > existingCount) {
      const newRowsNeeded = neededRows - existingCount
      const newItems = pasteData.slice(existingCount - startRow).map(data => ({
        description: (data.description as string) || '',
        quantity: (data.quantity as number) || 0,
        unit: (data.unit as string) || 'm2',
        location: (data.location as string) || '',
        productivity_pmpd: (data.productivity_pmpd as number) || 15,
      }))
      
      if (newItems.length > 0) {
        try {
          await quoteApi.bulkCreateLineItems(quoteId, sectionId, newItems)
        } catch (error) {
          console.error('Failed to create rows for paste:', error)
          throw error
        }
      }
    }
    
    // Stage patches for existing rows
    const updateRows = pasteData.slice(0, Math.min(existingCount - startRow, pasteData.length))
    updateRows.forEach((data, idx) => {
      const targetRow = startRow + idx
      const item = existingItems[targetRow]
      if (item) {
        stagePatch(sectionId, item.id, data)
      }
    })
  }, [quoteId, stagePatch])
  
  // Clear a specific error
  const clearError = useCallback((itemId: string, field: string) => {
    setState(prev => {
      const newErrors = new Map(prev.errors)
      newErrors.delete(`${itemId}:${field}`)
      return { ...prev, errors: newErrors }
    })
  }, [])
  
  // Clear all errors
  const clearAllErrors = useCallback(() => {
    setState(prev => ({ ...prev, errors: new Map() }))
  }, [])
  
  // Retry all errors
  const retryErrors = useCallback(async () => {
    await flush()
  }, [flush])
  
  // Get optimistic value
  const getOptimisticValue = useCallback((itemId: string, field: string): unknown | undefined => {
    const staged = state.staged.get(itemId)
    return staged?.patch[field]
  }, [state.staged])
  
  // Get cell error state
  const getCellErrorState = useCallback((itemId: string, field: string): CellErrorState => {
    if (state.isFlushing && state.staged.has(itemId)) {
      return 'retrying'
    }
    if (state.errors.has(`${itemId}:${field}`)) {
      return 'error'
    }
    return 'none'
  }, [state.staged, state.errors, state.isFlushing])
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])
  
  return {
    hasPendingChanges: state.staged.size > 0,
    pendingCount: state.staged.size,
    isFlushing: state.isFlushing,
    errors: Array.from(state.errors.values()),
    stagePatch,
    flush,
    clearError,
    clearAllErrors,
    retryErrors,
    stagePasteCreate,
    getOptimisticValue,
    getCellErrorState,
  }
}

